import * as React from "react";
import { StyleProps } from "../../utils/types";
import {
  CheckboxGroupProps,
  CheckboxGroupState,
  RadioGroupProps,
  RadioGroupState,
  useCheckboxGroupState,
  useRadioGroupState,
} from "react-stately";
import {
  AriaCheckboxGroupItemProps,
  AriaRadioProps,
  VisuallyHidden,
  mergeProps,
  useCheckboxGroup,
  useCheckboxGroupItem,
  useFocusRing,
  useRadio,
  useRadioGroup,
} from "react-aria";
import { cx } from "../../utils/dom";
import {
  BorderRadius,
  ButtonSize,
  Color,
  ColorVariant,
  SizeOption,
} from "../../utils/theme";
import { useTheme } from "../theme";
import { Space, useSpace } from "../space/space";
import { ButtonToggleProps } from "../button/button";
import { Field } from "../form/field";

// TODO: Figure out how to make this a whole lot simpler.
// We can't call hooks conditionally
// We don't want to have two separate components for single and multiple selection toggles

type Mode = "single" | "multiple";
type ValueType<M> = M extends "single" ? string : string[];

type CFGroupProps<P> = Omit<
  P,
  "onChange" | "value" | "defaultValue" | "validate"
>;

type CFUnion = CFGroupProps<RadioGroupProps> | CFGroupProps<CheckboxGroupProps>;

type ToggleGroupContextValue = {
  state: RadioGroupState | CheckboxGroupState;
  mode: "single" | "multiple";
  props: GroupProps<CFUnion>;
};
const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(
  null
);

type GroupProps<T> = StyleProps<"wrapper"> &
  ButtonToggleProps &
  T & {
    children?: React.ReactNode;
    radius?: BorderRadius;
    size?: ButtonSize;
  };

type ToggleGroupProps<M extends Mode> = GroupProps<CFUnion> & {
  mode?: M;
} & React.ComponentProps<typeof Space>;

type ValueProps<M extends Mode> = {
  value?: ValueType<M>;
  defaultValue?: ValueType<M>;
  onChange?: (value: ValueType<M>) => void;
  validate?: (value: ValueType<M>) => void;
};

export function ToggleGroup<M extends Mode = "single">({
  children,
  mode = "single" as M,
  value,
  defaultValue,
  onChange,
  validate,
  styles,
  classNames,
  className,
  style,
  ...props
}: ToggleGroupProps<M> & ValueProps<M>) {
  const Wrapper = React.useMemo(() => {
    if (mode === "single") {
      return SingleSelectGroup;
    }

    return MultiSelectGroup;
  }, [mode]);

  props = { ...props } as CFUnion;

  return (
    <Field label={props.label} replaceDefaultControlWrapper>
      <Wrapper
        value={value as any}
        defaultValue={defaultValue as any}
        onChange={onChange as any}
        validate={validate as any}
        {...props}
      >
        <Space
          className={className}
          styles={styles}
          classNames={classNames}
          style={style}
          {...props}
        >
          {children}
        </Space>
      </Wrapper>
    </Field>
  );
}

function SingleSelectGroup({
  children,
  orientation = "horizontal",
  ...props
}: GroupProps<RadioGroupProps>) {
  const state = useRadioGroupState(props);
  const { radioGroupProps } = useRadioGroup(
    { ...props, orientation, "aria-label": "single toggle group" },
    state
  );
  return (
    <div {...radioGroupProps}>
      <ToggleGroupContext.Provider value={{ state, mode: "single", props }}>
        {children}
      </ToggleGroupContext.Provider>
    </div>
  );
}

function MultiSelectGroup({
  children,
  ...props
}: GroupProps<CheckboxGroupProps>) {
  const state = useCheckboxGroupState(props);
  const { groupProps } = useCheckboxGroup(
    { ...props, "aria-label": "multiple toggle group" },
    state
  );
  return (
    <div {...groupProps}>
      <ToggleGroupContext.Provider value={{ state, mode: "multiple", props }}>
        {children}
      </ToggleGroupContext.Provider>
    </div>
  );
}

type SingleToggleProps<P> = P & {
  children: React.ReactNode;
};

function SingleToggle({ ...props }: SingleToggleProps<AriaRadioProps>) {
  const ctx = React.useContext(ToggleGroupContext);
  const state = ctx?.state as RadioGroupState;
  const ref = React.useRef(null);
  if (!ctx) {
    throw new Error("Toggle must be used within a ToggleGroup");
  }

  const { focusProps, isFocusVisible } = useFocusRing();
  const isSelected = state.selectedValue === props.value;

  const { inputProps } = useRadio(props, ctx.state as RadioGroupState, ref);

  return (
    <ToggleButton isSelected={isSelected} isFocusVisible={isFocusVisible}>
      <VisuallyHidden>
        <input ref={ref} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
      {props.children}
    </ToggleButton>
  );
}

function MultiToggle({
  ...props
}: SingleToggleProps<AriaCheckboxGroupItemProps>) {
  const ctx = React.useContext(ToggleGroupContext);
  const state = ctx?.state as CheckboxGroupState;
  const ref = React.useRef(null);
  if (!ctx) {
    throw new Error("Toggle must be used within a ToggleGroup");
  }

  const { focusProps, isFocusVisible } = useFocusRing();
  const isSelected = state.isSelected(props.value);

  const { inputProps } = useCheckboxGroupItem(
    props,
    ctx.state as CheckboxGroupState,
    ref
  );

  return (
    <ToggleButton isSelected={isSelected} isFocusVisible={isFocusVisible}>
      <VisuallyHidden>
        <input ref={ref} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
      {props.children}
    </ToggleButton>
  );
}

type ToggleProps = StyleProps<"wrapper"> &
  ButtonToggleProps & {
    value: string;
    children: React.ReactNode;
    color?: Color;
    variant?: ColorVariant;
    size?: SizeOption;
    radius?: BorderRadius;
    isIconButton?: boolean;
  };

const ToggleContext = React.createContext<{ props: ToggleProps } | null>(null);

export function Toggle({ ...props }: ToggleProps) {
  const ctx = React.useContext(ToggleGroupContext);
  if (!ctx) {
    throw new Error("Toggle must be used within a ToggleGroup");
  }

  const Wrapper = React.useMemo(() => {
    if (ctx?.mode === "single") {
      return SingleToggle;
    }

    return MultiToggle;
  }, [ctx?.mode]);

  return (
    <ToggleContext.Provider value={{ props }}>
      <Wrapper value={props.value}>{props.children}</Wrapper>
    </ToggleContext.Provider>
  );
}

function ToggleButton({
  children,
  isFocusVisible,
  isSelected,
  ...props
}: {
  children: React.ReactNode;
  isSelected?: boolean;
  isFocusVisible?: boolean;
}) {
  const ctx = React.useContext(ToggleContext)?.props;
  const groupCtx = React.useContext(ToggleGroupContext);

  if (!ctx || !groupCtx) {
    throw new Error("Toggle must be used within a ToggleGroup");
  }

  const theme = useTheme();
  const space = useSpace();
  const idleColor = ctx.idleColor ?? groupCtx.props.idleColor ?? "gray";
  const activeColor =
    ctx.activeColor ?? groupCtx.props.activeColor ?? theme?.color ?? "gray";
  const idleVariant = ctx.idleVariant ?? groupCtx.props.idleVariant ?? "light";
  const activeVariant =
    ctx.activeVariant ?? groupCtx.props.activeVariant ?? "solid";
  const color = isSelected ? activeColor : idleColor;
  const variant = isSelected ? activeVariant : idleVariant;
  const radius = ctx.radius ?? groupCtx.props.radius ?? "full";
  const size = ctx.size ?? groupCtx.props.size ?? "md";

  return (
    <label
      data-icon-button={ctx.isIconButton}
      data-is-toggle={true}
      data-radius={radius}
      data-color={color}
      data-variant={variant}
      className={cx(
        "p-button",
        ctx.className,
        ctx.classNames?.wrapper,
        `size-${size}`,
        space.classNames
      )}
      data-selected={isSelected}
      data-focus-visible={isFocusVisible}
      {...props}
    >
      {children}
    </label>
  );
}
